import puppeteer from 'puppeteer';
import type { Configuration } from '../types';
import { GenerationError } from '../types';

export class PuppeteerGenerator {
  private config: Configuration;

  constructor(config: Configuration) {
    this.config = config;
  }

  async generate(html: string): Promise<Buffer> {
    let browser: Awaited<ReturnType<typeof puppeteer.launch>> | null = null;

    try {
      console.log(
        `PuppeteerGenerator: Generating PDF from HTML - Format: ${this.config.page.format}`
      );

      // Launch browser with appropriate options
      browser = await puppeteer.launch({
        headless: true,
        args: [
          '--no-sandbox',
          '--disable-setuid-sandbox',
          '--disable-dev-shm-usage',
          '--disable-accelerated-2d-canvas',
          '--no-first-run',
          '--no-zygote',
          '--single-process',
          '--disable-gpu',
        ],
      });

      const page = await browser.newPage();

      try {
        // Set viewport for consistent rendering
        await page.setViewport({ width: 1200, height: 800 });

        // Set content with enhanced HTML
        const enhancedHtml = this.enhanceHtmlForPrint(html);
        await page.setContent(enhancedHtml, {
          waitUntil: 'networkidle0',
          timeout: 30000,
        });

        // Configure PDF generation options
        const pdfOptions = this.buildPDFOptions();

        console.log(`PuppeteerGenerator: Generating PDF with options:`, {
          format: pdfOptions.format,
          margin: pdfOptions.margin,
          printBackground: pdfOptions.printBackground,
        });

        // Generate PDF
        const pdfUint8Array = await page.pdf(pdfOptions);
        const pdfBuffer = Buffer.from(pdfUint8Array);

        console.log(`PuppeteerGenerator: Successfully generated PDF (${pdfBuffer.length} bytes)`);

        return pdfBuffer;
      } finally {
        await page.close();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('PuppeteerGenerator: PDF generation failed:', errorMessage);

      throw new GenerationError(`Puppeteer generation failed: ${errorMessage}`, {
        cause: error as Error,
      });
    } finally {
      if (browser) {
        await browser.close();
      }
    }
  }

  /**
   * Enhance HTML with print-specific styling
   */
  private enhanceHtmlForPrint(html: string): string {
    const printCSS = this.buildPrintCSS();

    // Inject print CSS into the HTML
    if (html.includes('</head>')) {
      return html.replace('</head>', `<style>${printCSS}</style>\n</head>`);
    } else if (html.includes('<head>')) {
      return html.replace('<head>', `<head>\n<style>${printCSS}</style>`);
    } else {
      return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>${printCSS}</style>
</head>
<body>
${html}
</body>
</html>`;
    }
  }

  /**
   * Build Puppeteer PDF options from configuration
   */
  private buildPDFOptions(): any {
    const margins = this.config.page.margins;

    return {
      format: this.mapPageFormat(this.config.page.format),
      landscape: this.config.page.orientation === 'landscape',
      printBackground: true,
      margin: {
        top: margins.top,
        right: margins.right,
        bottom: margins.bottom,
        left: margins.left,
      },
      preferCSSPageSize: true,
      timeout: 60000,
    };
  }

  /**
   * Map MDExport page format to Puppeteer format
   */
  private mapPageFormat(format: string): string {
    const formatMap: Record<string, string> = {
      A4: 'a4',
      A3: 'a3',
      A5: 'a5',
      Letter: 'letter',
      Legal: 'legal',
      Tabloid: 'tabloid',
    };

    return formatMap[format] || 'a4';
  }

  /**
   * Build print-specific CSS
   */
  private buildPrintCSS(): string {
    return `
@page {
  size: ${this.config.page.format} ${this.config.page.orientation};
  margin: 0;
}

@media print {
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  body {
    font-size: 11pt;
    line-height: 1.4;
  }

  h1, h2, h3, h4, h5, h6 {
    page-break-after: avoid;
    break-after: avoid;
  }

  pre, blockquote {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  img, figure {
    page-break-inside: avoid;
    break-inside: avoid;
    max-width: 100%;
  }

  table {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }
}

/* Typography improvements */
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: #333;
}

code {
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
  background-color: #f6f8fa;
  padding: 2px 4px;
  border-radius: 3px;
}

pre code {
  background: none;
  padding: 0;
}`;
  }
}
